import { Button } from "@/components/ui/baseUi/button"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { Text } from "@/components/ui/baseUi/text"
import useDebounce from "@/modules/hooks/useDebounce"
import { cn } from "@/modules/lib/utils"
import { FileUploadImage } from "@/modules/ui/components/fileUpload/fileUploadImage"
import { useCheckUsername } from "@/modules/users/hooks/useCheckUsername"
import { type SimpleFormik } from "@/typings/formik"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { AuthFormError } from "@modules/auth/components/authComponents"
import { type ProfileRegisterFormikType, type ProfileRegisterInputKey } from "@modules/auth/profileRegisterFormikTypes"
import { word } from "@modules/core/utils/i18n"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { type HUImage } from "@modules/types/imageTypes"
import { ArrowLeft, CameraIcon, Loader, PenIcon } from "lucide-react"
import { useMemo } from "react"

export type RegisterFormFourPageProps = {
  handleSubmit: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  onPrevious: () => void
  formik: SimpleFormik<ProfileRegisterFormikType, ProfileRegisterInputKey>
  getFormErrorMessage: (name: ProfileRegisterInputKey, errorMessage?: string) => React.JSX.Element | undefined
  disabled: boolean
  error: string
  setError: (value: string) => void
  isSubmitting: boolean
  className?: string
} & React.ComponentProps<"div">

export function RegisterFormFourPage({
  handleSubmit,
  onPrevious,
  formik,
  getFormErrorMessage,
  disabled,
  error,
  setError,
  isSubmitting,
  className,
}: RegisterFormFourPageProps) {
  const debouncedCheckUsername = useDebounce(formik.values.username, 500)
  const { data: isUsernameValid, isPending: isUsernameValidateLoading } = useCheckUsername(debouncedCheckUsername ?? "")

  const getUsernameError = () => {
    if (error) {
      return <AuthFormError>{error}</AuthFormError>
    }
    if (debouncedCheckUsername && !isUsernameValid && !isUsernameValidateLoading) {
      return <AuthFormError>{word("auth.error.username_exists")}</AuthFormError>
    }
    if (getFormErrorMessage("username")) {
      return getFormErrorMessage("username")
    }
  }

  const avatarImage: HUImage | undefined = useMemo(() => {
    const url = formik.values.avatar?.file
      ? URL.createObjectURL(formik.values.avatar.file)
      : (formik.values.avatar?.url ?? undefined)
    return url ? { url, hash: "", key: formik.values.avatar?.key ?? "" } : undefined
  }, [formik.values.avatar])

  return (
    <div className={cn("flex grow flex-col justify-between gap-3", className)}>
      <Button variant="ghost" size="xs" onClick={onPrevious} className="w-fit justify-start">
        <ArrowLeft />
        {word("global.previous")}
      </Button>

      <Text fontStyle="TM">4/4</Text>
      <Text fontStyle="TM">{word("auth.signup.form.page.4.title")}</Text>
      <Text fontStyle="BS" color="--neutral-700">
        {word("auth.signup.form.page.4.description")}
      </Text>

      <div className="mt-2 flex flex-col gap-3">
        <div className="rounded-2xl bg-surface-foreground">
          <FileUploadImage
            setImage={(e) => {
              void formik.setFieldValue("avatar", e)
            }}
            disabled={isSubmitting}
          >
            <div className="flex w-full flex-row justify-center">
              <div className="relative my-4 h-fit w-fit">
                <ProfileAvatar
                  profile={{ avatar: avatarImage ?? null, username: formik.values.username }}
                  className="size-25 **:text-5xl"
                />
                <div className="absolute right-1 bottom-1 rounded-full bg-neutral-900 p-2 ring-3 ring-neutral-50">
                  {formik.values.avatar ? (
                    <PenIcon className="size-4 text-textOnColor-body" />
                  ) : (
                    <CameraIcon className="size-4 text-textOnColor-body" />
                  )}
                </div>
              </div>
            </div>
          </FileUploadImage>
        </div>

        <StandardInput
          inputProps={{
            placeholder: word("auth.signup.form.username"),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              void formik.setFieldValue("username", e.target.value).then(() => {
                setError("")
                formik.validateField?.("username")
              })
            },
          }}
          isRequiredInput
          error={getUsernameError()}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          name="username"
        />
      </div>

      <div className="mb-2 flex flex-row items-center gap-2">
        <WarningHexagonalIcon className="size-8 text-neutral-600" color="currentColor" />

        <Text fontStyle="BM">{word("auth.signup.form.warning.pseudoMessage")}</Text>
      </div>

      <Button
        type="button"
        size="lg"
        variant="primary"
        onClick={(e) => {handleSubmit(e)}}
        disabled={
          disabled ||
          !isUsernameValid ||
          (!!debouncedCheckUsername && isUsernameValidateLoading) ||
          isSubmitting
        }
      >
        {((!!debouncedCheckUsername && isUsernameValidateLoading) || isSubmitting) && (
          <Loader className="animate-spin" />
        )}
        {word("global.letsgo")}
      </Button>
    </div>
  )
}
